import type { SubscriptionTableRow } from "../../SubscriptionTable/types";

export interface CellChange {
    consumptionCount?: number;
    avgConsumptionDuration?: number;
}

export interface TableCalculationsParams {
    rows: SubscriptionTableRow[]; // Current displayed rows (from previous iteration or backend initially)
    changedRows: Map<number, CellChange>;
    channelTotalRevenue: number;
    currentTotalSubscriptionPoints: number; // Total from previous iteration (or backend initially)
}

/**
 * Recalculates all computed columns for the table based on the PREVIOUS iteration and new changes.
 *
 * The rows parameter contains the CURRENT DISPLAYED state (either from backend initially, or from previous iteration).
 * We calculate delta from current displayed values to new values based on changedRows.
 *
 * Calculation flow:
 * 1. For changed rows: calculate new totalConsumptionTime and delta from CURRENT displayed value
 * 2. totalOfAllItems = currentTotalSubscriptionPoints + delta
 * 3. revenueShare = (totalConsumptionTime / totalOfAllItems) * channelTotalRevenue
 * 4. royaltiesAmount = (royaltiesPercent / 100) * revenueShare
 */
export const recalculateTableRows = ({
    rows,
    changedRows,
    channelTotalRevenue,
    currentTotalSubscriptionPoints,
}: TableCalculationsParams): { rows: SubscriptionTableRow[]; newTotalSubscriptionPoints: number } => {
    // Calculate delta from CURRENT displayed state (in rows) to new state (from changedRows)
    let totalDelta = 0;

    const updatedRows = rows.map((row) => {
        const changes = changedRows.get(Number(row.id));

        if (!changes) {
            // No changes for this row - keep current displayed state
            return row;
        }

        // Calculate new value from changes
        const newConsumptionCount = changes.consumptionCount ?? row.consumptionCount ?? 0;
        const newAvgConsumptionDuration = changes.avgConsumptionDuration ?? row.avgConsumptionDuration ?? 0;
        const coefficient = row.coefficient ?? 1;
        const newTotalConsumptionTime = newConsumptionCount * newAvgConsumptionDuration * coefficient;

        // Delta = new value - CURRENT displayed value (from row.totalConsumptionTime)
        const currentTotalConsumptionTime = row.totalConsumptionTime ?? 0;
        totalDelta += newTotalConsumptionTime - currentTotalConsumptionTime;

        return {
            ...row,
            consumptionCount: newConsumptionCount,
            avgConsumptionDuration: newAvgConsumptionDuration,
            totalConsumptionTime: newTotalConsumptionTime,
        };
    });

    // Add delta to current total (from previous iteration)
    const totalOfAllItems = currentTotalSubscriptionPoints + totalDelta;

    // Recalculate dependent fields for ALL rows
    const finalRows = updatedRows.map((row) => {
        const revenueShare =
            totalOfAllItems > 0 ? ((row.totalConsumptionTime ?? 0) / totalOfAllItems) * channelTotalRevenue : 0;

        // Only recalculate royalties for rows that were changed by the user
        // Otherwise preserve the original amounts from the API
        const updatedRoyalties = changedRows.has(Number(row.id))
            ? (row.royalties || []).map((royalty) => {
                  // Update amounts in valuesByPeriod based on new revenueShare
                  const updatedValuesByPeriod: Record<string, { percentage: number; amount: number }> = {};

                  if (royalty.valuesByPeriod) {
                      Object.entries(royalty.valuesByPeriod).forEach(([periodId, values]) => {
                          updatedValuesByPeriod[periodId] = {
                              percentage: values.percentage,
                              amount: (values.percentage / 100) * revenueShare,
                          };
                      });
                  }

                  return {
                      ...royalty,
                      valuesByPeriod: updatedValuesByPeriod,
                  };
              })
            : row.royalties || []; // Preserve original royalties for unchanged rows

        return {
            ...row,
            totalOfAllItems,
            revenueShare,
            royalties: updatedRoyalties,
        };
    });

    return {
        rows: finalRows,
        newTotalSubscriptionPoints: totalOfAllItems,
    };
};
